import React from 'react';
import Icon from '../../../components/AppIcon';
import DeadlineCountdown from '../../payroll-compliance/components/DeadlineCountdown';

const DeadlineTrackingPanel = ({ violations, scanResults }) => {
  // Calculate upcoming deadlines based on violations and scan results
  const getUpcomingDeadlines = () => {
    const deadlines = [];

    // Add NASFUND deadline
    const nasfundDeadline = new Date();
    nasfundDeadline?.setDate(nasfundDeadline?.getDate() + 7);
    deadlines?.push({
      id: 'nasfund',
      title: 'NASFUND Contribution Submission',
      description: 'Monthly NASFUND employer and employee contributions',
      deadline: nasfundDeadline?.toISOString(),
      priority: violations?.filter(v => v?.violationType?.includes('nasfund'))?.length > 0 ? 'critical' : 'high',
      violationCount: violations?.filter(v => v?.violationType?.includes('nasfund'))?.length || 0
    });

    // Add SWT deadline
    const swtDeadline = new Date();
    swtDeadline?.setDate(swtDeadline?.getDate() + 14);
    deadlines?.push({
      id: 'swt',
      title: 'SWT (Salary & Wages Tax) Filing',
      description: 'Monthly salary and wages tax submission to IRC',
      deadline: swtDeadline?.toISOString(),
      priority: violations?.filter(v => v?.violationType?.includes('swt'))?.length > 0 ? 'critical' : 'high',
      violationCount: violations?.filter(v => v?.violationType?.includes('swt'))?.length || 0
    });

    // Add payroll processing deadline
    const payrollDeadline = new Date();
    payrollDeadline?.setDate(payrollDeadline?.getDate() + 3);
    deadlines?.push({
      id: 'payroll',
      title: 'Payroll Processing Deadline',
      description: 'Complete payroll processing and compliance checks',
      deadline: payrollDeadline?.toISOString(),
      priority: violations?.filter(v => v?.severity === 'critical')?.length > 0 ? 'critical' : 'normal',
      violationCount: violations?.filter(v => v?.severity === 'critical')?.length || 0
    });

    return deadlines?.sort((a, b) => new Date(a.deadline) - new Date(b.deadline));
  };

  const upcomingDeadlines = getUpcomingDeadlines();

  return (
    <div className="space-y-6">
      {/* Deadline Summary */}
      <div className="bg-card border border-border rounded-lg p-4">
        <div className="flex items-center gap-2 mb-4">
          <Icon name="Clock" size={20} color="var(--color-primary)" />
          <h4 className="font-heading font-semibold text-base text-foreground">Deadline Summary</h4>
        </div>
        <div className="grid grid-cols-3 gap-4">
          <div className="text-center p-3 bg-error/10 rounded-lg">
            <p className="text-2xl font-bold text-error">
              {upcomingDeadlines?.filter(d => d?.priority === 'critical')?.length}
            </p>
            <p className="text-xs text-muted-foreground">Critical</p>
          </div>
          <div className="text-center p-3 bg-warning/10 rounded-lg">
            <p className="text-2xl font-bold text-warning">
              {upcomingDeadlines?.filter(d => d?.priority === 'high')?.length}
            </p>
            <p className="text-xs text-muted-foreground">High Priority</p>
          </div>
          <div className="text-center p-3 bg-muted rounded-lg">
            <p className="text-2xl font-bold text-foreground">
              {upcomingDeadlines?.length}
            </p>
            <p className="text-xs text-muted-foreground">Total</p>
          </div>
        </div>
      </div>
      {/* Deadline Countdowns */}
      <div>
        <h4 className="font-heading font-semibold text-base text-foreground mb-4">
          Upcoming Submission Deadlines
        </h4>
        <div className="space-y-4">
          {upcomingDeadlines?.map((deadline) => (
            <div key={deadline?.id}>
              <DeadlineCountdown
                deadline={deadline?.deadline}
                title={deadline?.title}
                description={deadline?.description}
                priority={deadline?.priority}
              />
              {deadline?.violationCount > 0 && (
                <div className="mt-2 p-3 bg-error/5 border border-error/20 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Icon name="AlertTriangle" size={16} color="var(--color-error)" />
                    <p className="text-xs font-medium text-error">
                      {deadline?.violationCount} violation{deadline?.violationCount !== 1 ? 's' : ''} must be resolved before submission
                    </p>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
      {/* Deadline-Sensitive Issues from AI Scan */}
      {scanResults?.deadlineSensitiveIssues && scanResults?.deadlineSensitiveIssues?.length > 0 && (
        <div className="bg-warning/5 border border-warning/20 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-3">
            <Icon name="AlertCircle" size={18} color="var(--color-warning)" />
            <h4 className="font-heading font-semibold text-sm text-foreground">
              AI-Detected Deadline Issues
            </h4>
          </div>
          <div className="space-y-2">
            {scanResults?.deadlineSensitiveIssues?.map((issue, index) => (
              <div key={index} className="bg-card rounded-lg p-3">
                <div className="flex items-start justify-between gap-2 mb-1">
                  <p className="text-sm font-medium text-foreground">{issue?.issue}</p>
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                    issue?.priority === 'critical' ? 'bg-error/10 text-error' :
                    issue?.priority === 'high' ? 'bg-warning/10 text-warning' : 'bg-primary/10 text-primary'
                  }`}>
                    {issue?.priority?.toUpperCase()}
                  </span>
                </div>
                {issue?.deadline && (
                  <p className="text-xs text-muted-foreground">
                    Deadline: {issue?.deadline}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
      {/* Automated Escalation */}
      <div className="bg-card border border-border rounded-lg p-4">
        <div className="flex items-center gap-2 mb-3">
          <Icon name="Bell" size={18} color="var(--color-primary)" />
          <h4 className="font-heading font-semibold text-sm text-foreground">Automated Escalation</h4>
        </div>
        <div className="space-y-2">
          <div className="flex items-center justify-between p-2 bg-muted/50 rounded">
            <span className="text-xs text-muted-foreground">Email notifications</span>
            <span className="text-xs font-medium text-success">Active</span>
          </div>
          <div className="flex items-center justify-between p-2 bg-muted/50 rounded">
            <span className="text-xs text-muted-foreground">Manager escalation (48h before)</span>
            <span className="text-xs font-medium text-success">Active</span>
          </div>
          <div className="flex items-center justify-between p-2 bg-muted/50 rounded">
            <span className="text-xs text-muted-foreground">Critical alert (24h before)</span>
            <span className="text-xs font-medium text-success">Active</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeadlineTrackingPanel;